// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE HAZARD FLIP.
//
// The finding is compositional change happening UNDER a shrinking total, and
// that is precisely the thing a bar chart of counts cannot show. So this view
// draws two things side by side and refuses to draw either alone:
//
//   * a 100% stacked area, which makes the crossover legible; and
//   * the absolute count on its own axis, because a rising SHARE reads as a
//     rising COUNT to almost every reader unless the falling total is placed
//     next to it. Showing only the share chart would be the classic share-chart
//     lie, and it would be a lie in the direction that makes recalls look like
//     an epidemic.
//
// The current year is drawn hatched and excluded from every comparison — it is
// a partial year and a partial year plotted as a completed one manufactures a
// collapse that has not happened.

import { getBundle } from '../data';
import { chartFrame, labelChart, linear, markTip, niceTicks, svgEl } from '../components/svg';
import { esc, num } from '../format';
import { glossaryButton } from '../glossary';
import { navigate } from '../router';

export function renderFlip(host: HTMLElement): void {
  const { stats, hazards, hazardById, meta } = getBundle();
  const years = stats.years;
  const partial = meta.accc.partialYear;
  const h = stats.headline;

  host.innerHTML = `
    <div class="view-head">
      <h2>What Australia recalls has changed more than how much it recalls</h2>
      <p>Every recall since ${meta.accc.seriesFromYear}, grouped by the kind of harm the notice describes.
      The share of recalls that are ${glossaryButton('buttonBattery', 'button battery')} hazards went from
      ${h.buttonBatteryFromShare}% in ${h.buttonBatteryFromYear} to ${h.buttonBatteryToShare}% in
      ${h.buttonBatteryToYear} — while the total number of recalls fell from ${num(h.peakTotal)} in
      ${h.peakYear} to ${num(h.latestFullTotal)} in ${h.latestFullYear}.</p>
    </div>
    <div class="grid-2" id="charts"></div>
    <div class="note caveat">
      <strong>Three things to hold in mind.</strong> The hazard class is
      ${glossaryButton('hazard', 'inferred')} from the wording of each notice — the ACCC publishes no hazard
      code — and ${pctOther(stats)}% of recalls fall in “Other” because their notice does not describe a
      hazard clearly enough to classify. <strong>Button batteries are the exception:</strong> that band comes
      from the ACCC's own category tag, which the ACCC applies retrospectively using today's taxonomy, so
      part of the rise is products being re-classified rather than newly recalled. And ${partial} is a
      partial year, drawn hatched here and excluded from every figure quoted above.
    </div>
  `;

  const charts = host.querySelector<HTMLElement>('#charts')!;
  charts.append(shareChart(years, hazards, partial));
  charts.append(totalChart(years, partial));

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.setAttribute('aria-label', 'Hazard classes');
  for (const hz of hazards) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'legend-item';
    item.innerHTML = `<span class="legend-swatch" style="background:${hz.colour}"></span><span>${esc(hz.label)}</span>`;
    markTip(item, `${hz.label}\n${hz.blurb}`);
    item.addEventListener('click', () => navigate('search', { h: hz.id }));
    item.setAttribute('aria-label', `${hz.label}. ${hz.blurb} Search recalls with this hazard.`);
    legend.append(item);
  }
  host.append(legend);
  void hazardById;
}

function pctOther(stats: ReturnType<typeof getBundle>['stats']): string {
  let other = 0;
  let all = 0;
  for (const y of stats.years) { other += y.byHazard[99] ?? 0; all += y.total; }
  return all ? ((100 * other) / all).toFixed(1) : '0';
}

const W = 640;
const H = 360;
const M = { top: 16, right: 16, bottom: 34, left: 46 };

function shareChart(
  years: ReturnType<typeof getBundle>['stats']['years'],
  hazards: ReturnType<typeof getBundle>['hazards'],
  partial: number,
): HTMLElement {
  const { figure, mount } = chartFrame({
    title: 'The mix of hazards, as a share of each year',
    subtitle: 'Each band is a hazard class. Bands always fill the year, so this shows composition, not volume.',
    scroll: true,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMidYMid meet' });
  svg.style.minWidth = '520px';
  labelChart(svg, 'Share of recalls by hazard class, by year',
    `A 100% stacked area chart covering ${years[0].year} to ${years[years.length - 1].year}, `
    + 'with one band per hazard class.');

  const x = linear([years[0].year, years[years.length - 1].year], [M.left, W - M.right]);
  const y = linear([0, 100], [H - M.bottom, M.top]);

  // Cumulative bands, bottom to top, in the fixed class order so a band never
  // swaps places between years.
  const order = hazards.map((hz) => hz.id);
  const baseline = new Map<number, number>(years.map((yr) => [yr.year, 0]));

  for (const hid of order) {
    const hz = hazards.find((z) => z.id === hid)!;
    const top: [number, number][] = [];
    const bottom: [number, number][] = [];
    for (const yr of years) {
      const share = yr.total ? (100 * (yr.byHazard[hid] ?? 0)) / yr.total : 0;
      const b = baseline.get(yr.year)!;
      bottom.push([yr.year, b]);
      top.push([yr.year, b + share]);
      baseline.set(yr.year, b + share);
    }
    const d = [
      ...top.map(([yy, v], i) => `${i ? 'L' : 'M'}${x(yy).toFixed(2)},${y(v).toFixed(2)}`),
      ...bottom.reverse().map(([yy, v]) => `L${x(yy).toFixed(2)},${y(v).toFixed(2)}`),
      'Z',
    ].join(' ');
    const path = svgEl('path', { d, fill: hz.colour, class: 'mark' });
    (path as unknown as SVGElement).style.opacity = '0.94';
    markTip(path, hz.label, hz.label);
    svg.append(path);
  }

  // Per-year hover targets: a band is hard to hit, and a reader wants the whole
  // year's breakdown rather than one band's value.
  const step = (W - M.left - M.right) / Math.max(1, years.length - 1);
  for (const yr of years) {
    const rect = svgEl('rect', {
      x: (x(yr.year) - step / 2).toFixed(2), y: M.top,
      width: step.toFixed(2), height: H - M.top - M.bottom,
      fill: 'transparent', class: 'mark',
    });
    const lines = hazards
      .map((hz) => ({ hz, n: yr.byHazard[hz.id] ?? 0 }))
      .filter((z) => z.n > 0)
      .sort((a, b) => b.n - a.n)
      .slice(0, 5)
      .map((z) => `${z.hz.label}: ${((100 * z.n) / yr.total).toFixed(1)}% (${z.n})`);
    markTip(rect,
      `${yr.year}${yr.year === partial ? ' (partial year)' : ''}\n${num(yr.total)} recalls\n${lines.join('\n')}`,
      `${yr.year}: ${num(yr.total)} recalls`);
    svg.append(rect);
  }

  if (years.some((yr) => yr.year === partial)) {
    // Built with createElementNS rather than innerHTML: assigning innerHTML on
    // an SVG element parses the markup in the HTML namespace in some engines,
    // producing elements that look right in the DOM inspector and render as
    // nothing at all. A pattern that silently fails to paint would remove the
    // only visual signal that the last year is incomplete.
    const defs = svgEl('defs');
    const pattern = svgEl('pattern', {
      id: 'hatch', width: 6, height: 6,
      patternTransform: 'rotate(45)', patternUnits: 'userSpaceOnUse',
    });
    pattern.append(
      svgEl('rect', { width: 6, height: 6, fill: 'rgba(251,250,248,.55)' }),
      svgEl('line', { x1: 0, y1: 0, x2: 0, y2: 6, stroke: 'rgba(28,26,23,.35)', 'stroke-width': 2 }),
    );
    defs.append(pattern);
    svg.prepend(defs);

    const px = x(partial);
    svg.append(svgEl('rect', {
      x: (px - step / 2).toFixed(2), y: M.top, width: (step / 2 + M.right).toFixed(2),
      height: H - M.top - M.bottom, fill: 'url(#hatch)', 'pointer-events': 'none',
    }));
  }

  axes(svg, x, y, years, (v) => `${v}%`, [0, 25, 50, 75, 100]);
  mount.append(svg);
  return figure;
}

function totalChart(
  years: ReturnType<typeof getBundle>['stats']['years'],
  partial: number,
): HTMLElement {
  const { figure, mount } = chartFrame({
    title: 'How many recalls a year',
    subtitle: 'The same years, in absolute numbers. The share chart alone would read as growth; this is the total it is a share of.',
    scroll: true,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMidYMid meet' });
  svg.style.minWidth = '520px';
  labelChart(svg, 'Total recalls per year',
    'A column chart of recall counts by year, peaking then falling by roughly half. The current partial year is hatched.');

  const max = Math.max(...years.map((yr) => yr.total));
  const x = linear([years[0].year, years[years.length - 1].year], [M.left, W - M.right]);
  const y = linear([0, max], [H - M.bottom, M.top]);
  const bw = Math.max(3, ((W - M.left - M.right) / years.length) * 0.72);

  for (const yr of years) {
    const isPartial = yr.year === partial;
    const rect = svgEl('rect', {
      x: (x(yr.year) - bw / 2).toFixed(2),
      y: y(yr.total).toFixed(2),
      width: bw.toFixed(2),
      height: Math.max(0, H - M.bottom - y(yr.total)).toFixed(2),
      fill: isPartial ? 'var(--border-strong)' : 'var(--accent-primary)',
      class: 'mark',
      rx: 2,
    });
    markTip(rect,
      `${yr.year}${isPartial ? ' — partial year, not comparable' : ''}\n${num(yr.total)} recalls`,
      `${yr.year}: ${num(yr.total)} recalls${isPartial ? ', partial year' : ''}`);
    svg.append(rect);
  }

  axes(svg, x, y, years, (v) => num(v), niceTicks(max, 4));
  mount.append(svg);
  return figure;
}

function axes(
  svg: SVGSVGElement,
  x: ReturnType<typeof linear>,
  y: ReturnType<typeof linear>,
  years: ReturnType<typeof getBundle>['stats']['years'],
  fmt: (v: number) => string,
  ticks: number[],
): void {
  for (const t of ticks) {
    svg.append(svgEl('line', { x1: M.left, x2: W - M.right, y1: y(t).toFixed(2), y2: y(t).toFixed(2), class: 'grid-line' }));
    const label = svgEl('text', { x: M.left - 6, y: (y(t) + 4).toFixed(2), class: 'axis-text', 'text-anchor': 'end' });
    label.textContent = fmt(t);
    svg.append(label);
  }
  svg.append(svgEl('line', { x1: M.left, x2: W - M.right, y1: y(y.domain[0]).toFixed(2), y2: y(y.domain[0]).toFixed(2), class: 'axis-line' }));

  const span = years[years.length - 1].year - years[0].year;
  const stepYears = span > 20 ? 5 : span > 10 ? 2 : 1;
  for (const yr of years) {
    if (yr.year % stepYears !== 0) continue;
    const t = svgEl('text', { x: x(yr.year).toFixed(2), y: H - M.bottom + 16, class: 'axis-text', 'text-anchor': 'middle' });
    t.textContent = String(yr.year);
    svg.append(t);
  }
}
